import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, Input, OnInit } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { ApiClientService, RightPanelService, ToastService } from '@services/index';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { CustomFormModule } from '@shared/custom-form/custom-form.module';
import { SharedModule } from '@shared/shared.module';
import { Device, DeviceFile } from 'app/types';
import type { GetDeviceResponse } from '@services/api-client/responses/get-device-response.type';
import { Permission } from 'app/enums/permission.enum';
import { TranslateService } from '@ngx-translate/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ConfirmModalComponent } from '@shared/components/confirm-modal/confirm-modal.component';
import { ModalHelper } from '@helpers/modal-helper';
import { EMPTY, finalize, switchMap, tap } from 'rxjs';

@UntilDestroy()
@Component({
    selector: 'app-create-and-update-device-modal',
    standalone: true,
    imports: [CommonModule, ReactiveFormsModule, TranslateModule, CustomFormModule, SharedModule],
    templateUrl: './create-and-update-device-modal.component.html',
    styleUrl: './create-and-update-device-modal.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class CreateAndUpdateDeviceModalComponent implements OnInit {
    private readonly changeDetectorRef = inject(ChangeDetectorRef);
    private readonly rightPanelService = inject(RightPanelService);
    private readonly apiClientService = inject(ApiClientService);
    private readonly modalService = inject(NgbModal);
    private readonly toastService = inject(ToastService);
    private readonly translateService = inject(TranslateService);

    @Input() public editDevice?: Device;

    public showPotentialValidationMessages = false;
    public loading: boolean = false;
    public clearingCalibration = false;

    public device: GetDeviceResponse | null = null;
    public permissions = Permission;

    public ngOnInit() {
        if (this.editDevice) {
            this.apiClientService
                .getDevice$(this.editDevice.id)
                .pipe(untilDestroyed(this))
                .subscribe((device) => {
                    this.device = {
                        ...device,
                        deviceFiles: [...(device.deviceFiles ?? [])].sort(
                            (a, b) => new Date(b.created).getTime() - new Date(a.created).getTime()
                        )
                    };
                    this.changeDetectorRef.markForCheck();
                });
        }
    }

    public close() {
        this.rightPanelService.hide();
    }

    public confirmClearCalibration() {
        const device = this.device;
        if (!device?.calibration || this.clearingCalibration) return;

        const modalRef = this.modalService.open(ConfirmModalComponent);
        const modalInstanceHelper = ModalHelper.getConfirmModalInstanceHelper(modalRef);
        modalInstanceHelper.componentInstance.model = {
            titleLanguageKey: 'FEATURES.DEVICE.CLEAR_CALIBRATION_TITLE',
            bodyLanguageKey: 'FEATURES.DEVICE.CLEAR_CALIBRATION_CONFIRM',
            bodyParams: { serialNumber: device.serialNumber },
            cancelLanguageKey: 'BUTTONS.CANCEL',
            confirmLanguageKey: 'BUTTONS.CONFIRM'
        };

        modalInstanceHelper.confirmation$
            .pipe(
                untilDestroyed(this),
                switchMap((confirmed) => {
                    if (!confirmed) return EMPTY;

                    this.clearingCalibration = true;
                    this.changeDetectorRef.markForCheck();
                    return this.apiClientService.clearDeviceCalibration$(device.id).pipe(
                        tap(() => {
                            if (this.device?.id === device.id) {
                                this.device = { ...this.device, calibration: null };
                            }
                        }),
                        finalize(() => {
                            this.clearingCalibration = false;
                            this.changeDetectorRef.markForCheck();
                        })
                    );
                })
            )
            .subscribe({
                next: () => this.showCalibrationClearToast('SUCCESS'),
                error: () => this.showCalibrationClearToast('ERROR')
            });
    }

    private showCalibrationClearToast(result: 'SUCCESS' | 'ERROR') {
        const title = this.translateService.instant(`TOAST_TITLES.${result}`) as string;
        const message = this.translateService.instant(`FEATURES.DEVICE.CLEAR_CALIBRATION_${result}`) as string;
        this.toastService.open(title, message, { type: result === 'SUCCESS' ? 'success' : 'error', progressBar: true });
    }

    public downloadLogFile(logfile: DeviceFile) {
        this.apiClientService.downloadDeviceLogFile$(this.editDevice.id, logfile.id).subscribe((response) => {
            const blob = response.body;
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = logfile.filename;
            a.click();
            window.URL.revokeObjectURL(url);
        });
    }
}
